import { useEffect, useState, useMemo } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import {
  CurrencyDollar, MapTrifold, Sparkle, Copy, ArrowsClockwise,
  TrendUp, ChatCircleDots, MagnifyingGlass, FilePdf, EnvelopeSimple,
  X as XIcon, PaperPlaneTilt, Eye, DownloadSimple, NotePencil, Clock,
} from "@phosphor-icons/react";

/**
 * BrokerRateCardPage — the "live during a broker call" pricing screen.
 *
 * Renders the (customer × lane) combos we've delivered ≥2 loads on,
 * with rate range, on-time %, and a deterministic counter-offer
 * suggestion. Optionally requests an AI-generated coaching line on
 * demand — kept off the main list to preserve fast first paint.
 *
 * UX priorities:
 *   - Cards, not table — owners will use this on mobile during a call
 *   - Copy-to-clipboard for the "counter" range because that's the
 *     ONE thing they need to say
 *   - Search + customer filter — a fleet with 100+ combos needs to
 *     find "Uber Freight TX→GA" in <5s
 */
export default function BrokerRateCardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [narrations, setNarrations] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [threadNoteFor, setThreadNoteFor] = useState(null);

  const loadThreads = async () => {
    try {
      const { data: t } = await client.get("/rate-card-threads?limit=25");
      setThreads(t.threads || []);
    } catch {
      /* silent — the threads panel is optional context */
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { data: d } = await client.get("/broker-rate-card");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rate card");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); loadThreads(); }, []);

  const filtered = useMemo(() => {
    const combos = data?.combos || [];
    const q = query.trim().toLowerCase();
    if (!q) return combos;
    return combos.filter((c) =>
      c.customer.toLowerCase().includes(q) ||
      c.lane.toLowerCase().includes(q),
    );
  }, [data, query]);

  const runAiNarrate = async (combo) => {
    const key = `${combo.customer}||${combo.lane}`;
    if (narrations[key] || busyKey === key) return;
    setBusyKey(key);
    try {
      const laneParam = combo.lane.replace(/ /g, "_");
      const { data: r } = await client.get(
        `/broker-rate-card/narrate/${encodeURIComponent(combo.customer)}/${encodeURIComponent(laneParam)}`,
      );
      if (r?.ai_narration) {
        setNarrations((prev) => ({ ...prev, [key]: r.ai_narration }));
      } else {
        toast.info("AI polish isn't available for this combo yet.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI polish failed");
    } finally {
      setBusyKey(null);
    }
  };

  const copyCounter = async (combo) => {
    const text = `$${combo.counter_offer.low.toLocaleString()}–$${combo.counter_offer.high.toLocaleString()}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied: ${text}`);
    } catch {
      toast.error("Copy failed — you can select the text manually");
    }
  };

  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await client.get("/broker-rate-card/pdf", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to pull the server-suggested filename from Content-Disposition
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      a.download = match?.[1] || "LanePerformance.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded — ready to email");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="broker-rate-card-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="pill pill-primary mb-2 inline-flex items-center gap-1.5">
            <CurrencyDollar size={11} weight="fill" /> BROKER RATE CARD
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight">
            Live pricing intelligence
          </h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">
            Every customer × lane you've delivered ≥2 loads on, with a data-backed counter-offer suggestion. Open this during a broker call.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <button
            onClick={() => setPreviewOpen(true)}
            disabled={!data || data.count === 0}
            data-testid="rate-card-preview-open"
            className="rounded-full px-4 py-2 text-xs border border-white/10 bg-white/5 text-neutral-200 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors"
            title={data?.count === 0 ? "Deliver at least 2 loads on a lane to enable" : "Preview the PDF before sending"}
          >
            <Eye size={12} weight="fill" />
            Preview
          </button>
          <button
            onClick={() => setEmailOpen(true)}
            disabled={!data || data.count === 0}
            data-testid="rate-card-email-open"
            className="rounded-full px-4 py-2 text-xs border border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors"
            title={data?.count === 0 ? "Deliver at least 2 loads on a lane to enable" : "Email the rate card PDF to a shipper contact"}
          >
            <EnvelopeSimple size={12} weight="fill" />
            Email to shipper
          </button>
          <button
            onClick={exportPdf}
            disabled={exporting || !data || data.count === 0}
            data-testid="rate-card-export-pdf"
            className="rounded-full px-4 py-2 text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors"
            title={data?.count === 0 ? "Deliver at least 2 loads on a lane to enable export" : "Download a shipper-facing lane performance sheet"}
          >
            <FilePdf size={12} weight="fill" />
            {exporting ? "Building…" : "Download PDF"}
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            data-testid="rate-card-refresh"
            className="btn-secondary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
          >
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by customer or lane (e.g. 'Uber' or 'TX')"
          className="w-full bg-[#141414] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-neutral-500 focus:border-[#FF5A1F]/60 focus:outline-none"
          data-testid="rate-card-search"
        />
      </div>

      {/* States */}
      {loading && !data && (
        <div className="card-surface p-8 text-center text-neutral-400">Loading…</div>
      )}

      {!loading && data && data.count === 0 && (
        <div className="card-surface p-8 text-center" data-testid="rate-card-empty">
          <div className="mb-3 text-neutral-400">No rate cards yet.</div>
          <div className="text-xs text-neutral-500">
            Once you've delivered ≥2 loads for the same customer on the same lane, we'll surface pricing intelligence here.
          </div>
        </div>
      )}

      {/* Combo cards */}
      {!loading && filtered.length > 0 && (
        <div className="space-y-3" data-testid="rate-card-list">
          {filtered.map((combo, i) => {
            const key = `${combo.customer}||${combo.lane}`;
            const onTime = combo.on_time_pct;
            const otColor = onTime == null
              ? "text-neutral-500"
              : onTime >= 90 ? "text-emerald-400"
              : onTime >= 75 ? "text-amber-400"
              : "text-red-400";

            return (
              <div
                key={key}
                data-testid={`rate-card-combo-${i}`}
                className="card-surface p-5 sm:p-6 hover:border-[#FF5A1F]/30 transition-colors"
              >
                {/* Top row: customer + lane + on-time */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="min-w-0">
                    <div className="font-display text-lg sm:text-xl font-semibold text-white truncate">
                      {combo.customer}
                    </div>
                    <div className="text-neutral-400 text-sm mt-0.5 font-mono inline-flex items-center gap-1.5">
                      <MapTrifold size={12} className="text-[#FF5A1F]" />
                      {combo.lane}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-mono text-2xl font-bold leading-none ${otColor}`}>
                      {onTime == null ? "—" : `${onTime}%`}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
                      on-time · {combo.loads_delivered} loads
                    </div>
                  </div>
                </div>

                {/* Rate range strip */}
                <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3 mb-3">
                  <div className="overline mb-2">Historical rates (last 180 days)</div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-[10px] uppercase text-neutral-500 tracking-widest">Min</div>
                      <div className="text-white font-mono tabular-nums">${combo.rate_min.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-neutral-500 tracking-widest">Avg</div>
                      <div className="text-white font-mono tabular-nums font-bold">${combo.rate_avg.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-neutral-500 tracking-widest">Max</div>
                      <div className="text-white font-mono tabular-nums">${combo.rate_max.toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                {/* Counter offer — the money shot */}
                <div className="rounded-lg bg-[#FF5A1F]/[0.08] border border-[#FF5A1F]/30 p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="overline text-[#FF5A1F] inline-flex items-center gap-1.5">
                      <TrendUp size={11} weight="fill" /> Suggested Counter
                    </div>
                    <button
                      onClick={() => copyCounter(combo)}
                      className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white inline-flex items-center gap-1"
                      data-testid={`rate-card-copy-${i}`}
                    >
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                  <div className="text-2xl sm:text-3xl font-display font-bold text-white tabular-nums">
                    ${combo.counter_offer.low.toLocaleString()} <span className="text-neutral-500">–</span> ${combo.counter_offer.high.toLocaleString()}
                  </div>
                  <div className="text-xs text-neutral-300 mt-2 leading-relaxed">
                    {combo.counter_offer.reasoning}
                  </div>

                  {/* AI polish */}
                  {narrations[key] ? (
                    <div className="mt-3 pt-3 border-t border-white/10 text-xs text-[#FF5A1F] inline-flex items-start gap-1.5" data-testid={`rate-card-ai-${i}`}>
                      <Sparkle size={11} weight="fill" className="mt-0.5 shrink-0" />
                      <span className="text-white/90">{narrations[key]}</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => runAiNarrate(combo)}
                      disabled={busyKey === key}
                      className="mt-3 text-[10px] uppercase tracking-widest text-[#FF5A1F] hover:text-white inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid={`rate-card-ai-btn-${i}`}
                    >
                      <Sparkle size={11} weight="fill" />
                      {busyKey === key ? "Thinking…" : "Get AI coaching line"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sent rate cards — email thread log */}
      {threads.length > 0 && (
        <div className="mt-8" data-testid="rate-card-threads-panel">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
            <EnvelopeSimple size={12} weight="fill" className="text-violet-400" />
            Sent rate cards · {threads.length}
          </div>
          <div className="card-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3">Recipient</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-right px-4 py-3">Replies</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody data-testid="rate-card-threads-list">
                {threads.map((t) => (
                  <tr
                    key={t.id}
                    className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors"
                    data-testid={`rate-card-thread-${t.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-white font-mono text-xs truncate max-w-xs" title={t.to_email}>
                        {t.to_email}
                      </div>
                      {t.note_at_send && (
                        <div className="text-[11px] text-neutral-500 mt-0.5 line-clamp-1 max-w-md">
                          "{t.note_at_send}"
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-400 text-xs">
                      <div className="inline-flex items-center gap-1">
                        <Clock size={11} />
                        {t.sent_at ? new Date(t.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(t.reply_count || 0) > 0 ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs rounded-full bg-emerald-500/10 text-emerald-300 px-2 py-0.5 border border-emerald-500/30"
                          data-testid={`rate-card-thread-replies-${t.id}`}
                        >
                          {t.reply_count} logged
                        </span>
                      ) : (
                        <span className="text-neutral-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setThreadNoteFor(t)}
                        data-testid={`rate-card-thread-log-${t.id}`}
                        className="text-xs rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20 px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                      >
                        <NotePencil size={11} weight="fill" />
                        Log reply
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Footer meta */}
      {data && (
        <div className="mt-6 text-xs text-neutral-500 text-center inline-flex items-center gap-1.5 w-full justify-center">
          <ChatCircleDots size={11} />
          Rate windows: {data.lookback_days} days. Counter-offers factor in your on-time record on each lane.
        </div>
      )}

      {emailOpen && (
        <RateCardEmailModal
          topCount={Math.min(3, data?.count || 0)}
          onClose={() => setEmailOpen(false)}
          onSent={() => { loadThreads(); }}
        />
      )}

      {previewOpen && (
        <RateCardPreviewModal
          onClose={() => setPreviewOpen(false)}
          onDownload={() => { setPreviewOpen(false); exportPdf(); }}
          onEmail={() => { setPreviewOpen(false); setEmailOpen(true); }}
        />
      )}

      {threadNoteFor && (
        <RateCardThreadNoteModal
          thread={threadNoteFor}
          onClose={() => setThreadNoteFor(null)}
          onLogged={() => { setThreadNoteFor(null); loadThreads(); }}
        />
      )}
    </div>
  );
}

function RateCardPreviewModal({ onClose, onDownload, onEmail }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cleanup = null;
    (async () => {
      try {
        const res = await client.get(
          "/broker-rate-card/pdf?disposition=inline",
          { responseType: "blob" },
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        cleanup = () => window.URL.revokeObjectURL(url);
      } catch (e) {
        setError(e?.response?.data?.detail || "Preview failed");
        toast.error("Couldn't build the PDF preview");
      }
    })();
    return () => { if (cleanup) cleanup(); };
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md p-4 overflow-y-auto"
      data-testid="rate-card-preview-modal"
    >
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-4xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden flex flex-col" style={{ maxHeight: "90vh" }}>
          <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-0.5 inline-flex items-center gap-1.5">
                <Eye size={11} weight="fill" /> RATE CARD PREVIEW
              </div>
              <div className="text-base font-semibold text-white">
                Review before you send
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="rate-card-preview-close"
              className="text-neutral-500 hover:text-white"
            >
              <XIcon size={18} />
            </button>
          </div>

          <div className="flex-1 min-h-0 bg-neutral-900">
            {error ? (
              <div className="h-full flex items-center justify-center p-8 text-center text-red-400 text-sm"
                   data-testid="rate-card-preview-error">
                {error}
              </div>
            ) : !pdfUrl ? (
              <div className="h-full flex items-center justify-center p-8 text-center text-neutral-400 text-sm"
                   data-testid="rate-card-preview-loading">
                Building preview…
              </div>
            ) : (
              <iframe
                src={pdfUrl}
                title="Rate Card PDF Preview"
                className="w-full h-full block"
                style={{ minHeight: "60vh", border: "none" }}
                data-testid="rate-card-preview-iframe"
              />
            )}
          </div>

          <div className="p-3 border-t border-white/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
            <div className="text-xs text-neutral-500 leading-relaxed">
              Preview only — no audit stamp until you download or email.
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={onClose}
                data-testid="rate-card-preview-cancel"
                className="text-sm text-neutral-400 hover:text-white px-3 py-2"
              >
                Close
              </button>
              <button
                onClick={onDownload}
                disabled={!pdfUrl}
                data-testid="rate-card-preview-download"
                className="text-sm rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40 px-3 py-2 inline-flex items-center gap-1.5 font-semibold"
              >
                <DownloadSimple size={13} weight="fill" />
                Download
              </button>
              <button
                onClick={onEmail}
                disabled={!pdfUrl}
                data-testid="rate-card-preview-email"
                className="text-sm rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white px-3 py-2 inline-flex items-center gap-1.5 font-semibold"
              >
                <PaperPlaneTilt size={13} weight="fill" />
                Email to shipper
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RateCardEmailModal({ topCount, onClose, onSent }) {
  const [toEmail, setToEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim());

  const send = async () => {
    if (!validEmail || sending) return;
    setSending(true);
    try {
      const { data } = await client.post("/broker-rate-card/email", {
        to_email: toEmail.trim(),
        note: note.trim(),
      });
      toast.success(`Rate card sent to ${data.to_email}`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed — try downloading and emailing manually");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
      data-testid="rate-card-email-modal"
    >
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold mb-1 inline-flex items-center gap-1.5">
                <EnvelopeSimple size={11} weight="fill" /> EMAIL RATE CARD
              </div>
              <div className="text-lg font-semibold text-white">Send to shipper</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Attaches your one-page PDF + previews top {topCount} lane{topCount === 1 ? "" : "s"} in the body
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="rate-card-email-close"
              className="text-neutral-500 hover:text-white"
            >
              <XIcon size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">
                Recipient email
              </label>
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="dispatch@shipperco.com"
                data-testid="rate-card-email-to"
                className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm py-2.5 px-3 focus:outline-none focus:border-violet-500/60"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">
                Optional note
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Hi Sarah — quick follow-up on the TX→GA quote. Numbers below."
                data-testid="rate-card-email-note"
                rows={4}
                maxLength={500}
                className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm p-3 focus:outline-none focus:border-violet-500/60 resize-y"
              />
              <div className="text-[10px] text-neutral-500 mt-1 text-right tabular-nums">
                {note.length} / 500
              </div>
            </div>
            <div className="rounded-lg bg-violet-500/[0.08] border border-violet-500/30 p-3 text-xs text-violet-100/90 leading-relaxed">
              The email replies come back to <b>your account address</b>. The PDF is regenerated fresh on send so any newly-delivered loads are included.
            </div>
          </div>

          <div className="p-4 border-t border-white/10 flex justify-end gap-2">
            <button
              onClick={onClose}
              data-testid="rate-card-email-cancel"
              className="text-sm text-neutral-400 hover:text-white px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={!validEmail || sending}
              data-testid="rate-card-email-send"
              className="text-sm rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 inline-flex items-center gap-1.5"
            >
              <PaperPlaneTilt size={13} weight="fill" />
              {sending ? "Sending…" : "Send rate card"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RateCardThreadNoteModal({ thread, onClose, onLogged }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!body.trim() || saving) return;
    setSaving(true);
    try {
      await client.post(`/rate-card-threads/${thread.id}/note`, {
        body: body.trim(),
      });
      toast.success("Reply logged");
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log reply");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
      data-testid="rate-card-thread-note-modal"
    >
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold mb-1 inline-flex items-center gap-1.5">
                <NotePencil size={11} weight="fill" /> LOG REPLY
              </div>
              <div className="text-base font-semibold text-white truncate">
                {thread.to_email}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">
                Sent {thread.sent_at ? new Date(thread.sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="rate-card-thread-note-close"
              className="text-neutral-500 hover:text-white"
            >
              <XIcon size={18} />
            </button>
          </div>

          {(thread.reply_notes || []).length > 0 && (
            <div className="px-5 pt-4 pb-2 max-h-52 overflow-y-auto">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
                Previously logged
              </div>
              <div className="space-y-2">
                {thread.reply_notes.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`rate-card-thread-existing-note-${n.id}`}
                    className="rounded-lg bg-white/[0.02] border border-white/5 p-3"
                  >
                    <div className="text-[11px] text-neutral-500 mb-1">
                      {n.logged_at ? new Date(n.logged_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                    </div>
                    <div className="text-xs text-neutral-200 whitespace-pre-wrap leading-relaxed">
                      {n.body}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-neutral-400 mb-1.5">
                What did they say?
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Paste the shipper's reply, or note what they said on the call. Everything stays private to your fleet."
                data-testid="rate-card-thread-note-body"
                rows={5}
                maxLength={2000}
                className="w-full bg-black/40 border border-white/10 rounded-lg text-white text-sm p-3 focus:outline-none focus:border-violet-500/60 resize-y"
                autoFocus
              />
              <div className="text-[10px] text-neutral-500 mt-1 text-right tabular-nums">
                {body.length} / 2000
              </div>
            </div>
            <div className="rounded-lg bg-violet-500/[0.08] border border-violet-500/30 p-3 text-xs text-violet-100/90 leading-relaxed">
              Full inbound reply capture is coming — for now, paste the shipper's answer here so it lives with the send.
            </div>
          </div>

          <div className="p-4 border-t border-white/10 flex justify-end gap-2">
            <button
              onClick={onClose}
              data-testid="rate-card-thread-note-cancel"
              className="text-sm text-neutral-400 hover:text-white px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={!body.trim() || saving}
              data-testid="rate-card-thread-note-save"
              className="text-sm rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-4 py-2 inline-flex items-center gap-1.5"
            >
              <NotePencil size={13} weight="fill" />
              {saving ? "Logging…" : "Log reply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
